import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AddOrRemoveToWaitlistActionEnum, PatientDto, WaitListEntryDto } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { patientKeys } from "./patient.keys";
import { notesKeys } from "../Notes/notes.keys";
import notificationStore from "../Notification/notificationStore";

/**
 * Hook for creating or updating a patient
 * Replaces PatientsStore.postPatients
 */
export const useCreateOrUpdatePatient = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientData: PatientDto) => {
      return await patients.postPatients(patientData);
    },
    onSuccess: (data) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: patientKeys.allPatients.all() });
      queryClient.invalidateQueries({ queryKey: patientKeys.patient.detail(data.patient?.id!) });
      // Invalidate patient notes so system notes (Systemhinweise) are refetched after validation changes
      queryClient.invalidateQueries({ queryKey: notesKeys.patientNotes.detail(data.patient?.id!.toString()) });
      
      // Always show success message since the original store logic shows it regardless
      notificationStore.showMessage("Patient wurde gespeichert", "success");
    },
    onError: (error: any) => {
      // console.log("Error during patient mutation:", error);
      const errorMessage = 
        error.response?.data?.message || error?.message || "Fehler bei der Patientenaktualisierung";
      notificationStore.showMessage(errorMessage, "error", error);
    },
  });
};

/**
 * Hook for adding/removing patient from waitlist
 * Replaces PatientsStore.addOrRemoveToWaitlist
 */

export const useAddOrRemoveFromWaitlist = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, waitListDto,action }: { id: number; action:AddOrRemoveToWaitlistActionEnum;waitListDto: WaitListEntryDto[] }) => {
      return await patients.addOrRemoveToWaitlist(id,action,waitListDto);
    },
    onSuccess: (data, variables) => {
      const message = variables.waitListDto.some(entry => entry.isWaiting)
        ? "Patient zur Warteliste hinzugefügt"
        : "Patient von der Warteliste entfernt";
      notificationStore.showMessage(message, "success");
      // Invalidate patient data
      queryClient.invalidateQueries({
        queryKey: patientKeys.patient.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: patientKeys.patientWaitlistStats.detail(variables.id),
      });
    },
    onError: (error: any) => {
      const errorMessage =

        error.response?.data?.message || "Fehler beim Hinzufügen des Patienten zur Warteliste!";
      notificationStore.showMessage(errorMessage, "error", error);
    },
  });
}
// export const useWaitlistMutation = () => {
//   const { patients } = useApi();
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: async ({ id, waitList }: { id: number; waitList: boolean }) => {
//       return await patients.addOrRemoveToWaitlist(id, waitList);
//     },
//     onSuccess: (data, variables) => {
//       const message = variables.waitList 
//         ? "Patient zur Warteliste hinzugefügt" 
//         : "Patient von der Warteliste entfernt";
//       notificationStore.showMessage(message, "success");
      
//       // Invalidate patient data
//       queryClient.invalidateQueries({ 
//         queryKey: patientKeys.patient.detail(variables.id) 
//       });
//     },
//     onError: (error: any) => {
//       const errorMessage = 
//         error.response?.data?.message || "Fehler beim Hinzufügen des Patienten zur Warteliste!";
//       notificationStore.showMessage(errorMessage, "error", error);
//     },
//   });
// };

/**
 * Hook for archiving a patient
 * Replaces PatientsStore.archivePatient
 */
export const useArchivePatient = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: number) => {
      return await patients.softDeletePatient(patientId);
    },
    onSuccess: (data, patientId) => {
      notificationStore.showMessage("Patient erfolgreich archiviert", "success");
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: patientKeys.allPatients.all() });
      queryClient.invalidateQueries({ queryKey: patientKeys.archivedPatients.all() });
      queryClient.removeQueries({ queryKey: patientKeys.patient.detail(patientId) });
    },
    onError: (error: any) => {
      notificationStore.showMessage("Fehler beim Archivieren des Patienten", "error", error);
    },
  });
};

/**
 * Hook for restoring an archived patient
 * Replaces PatientsStore.restorePatient
 */
export const useRestorePatient = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: number) => {
      return await patients.restorePatient(patientId);
    },
    onSuccess: (data, patientId) => {
      notificationStore.showMessage("Patient erfolgreich wiederhergestellt", "success");
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: patientKeys.allPatients.all() });
      queryClient.invalidateQueries({ queryKey: patientKeys.archivedPatients.all() });
      queryClient.invalidateQueries({ queryKey: patientKeys.patient.detail(patientId) });
    },
    onError: (error: any) => {
      notificationStore.showMessage("Fehler beim Wiederherstellen des Patienten", "error", error);
    },
  });
};

/**
 * Hook for permanently deleting a patient
 * Replaces PatientsStore.permanentlyDeletePatient
 */
export const usePermanentDeletePatient = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ patientId, confirmationText }: { patientId: number; confirmationText: string }) => {
      return await patients.hardDeletePatient(patientId, { confirmationText });
    },
    onSuccess: (data, { patientId }) => {
      notificationStore.showMessage("Patient endgültig gelöscht", "success");
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: patientKeys.archivedPatients.all() });
      queryClient.removeQueries({ queryKey: patientKeys.patient.detail(patientId) });
    },
    onError: (error: any) => {
      const errorMessage = 
        error?.response?.data?.message || "Fehler beim endgültigen Löschen des Patienten";
      notificationStore.showMessage(errorMessage, "error", error);
    },
  });
};

/**
 * Hook for deleting a patient address
 * Replaces PatientsStore.deletePatientAddress
 */
export const useDeletePatientAddress = () => {
  const { patients } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addressId: string) => {
      return await patients.deleteAddress(addressId);
    },
    onSuccess: () => {
      // You might want to invalidate patient queries if you know which patient was affected
      queryClient.invalidateQueries({ queryKey: patientKeys.allPatients.all() });
    },
    onError: (error: any) => {
      notificationStore.showMessage("Fehler beim Löschen der Patientenadresse", "error", error);
    },
  });
};
